import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { posRequestsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { ShoppingBag, CheckCircle, XCircle, Eye, Building, MapPin, Phone, User } from 'lucide-react';

const PosRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);

    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        try {
            const response = await posRequestsAPI.getAll();
            setRequests(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch POS requests');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (requestId, status) => {
        try {
            await posRequestsAPI.update(requestId, { status });
            toast.success(`Request ${status} successfully`);
            fetchRequests(); // Refresh the list
            setShowDetailsDialog(false);
        } catch (error) {
            toast.error(`Failed to ${status} request`);
        }
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: { variant: 'secondary', color: 'bg-amber-100 text-amber-800', icon: null },
            approved: { variant: 'success', color: 'bg-green-100 text-green-800', icon: CheckCircle },
            rejected: { variant: 'destructive', color: 'bg-red-100 text-red-800', icon: XCircle }
        };

        const config = variants[status] || variants.pending;
        const Icon = config.icon;

        return (
            <Badge className={config.color}>
                {Icon && <Icon className="w-3 h-3 mr-1" />}
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                    <p className="text-muted-foreground">Loading POS requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">POS Access Requests</h1>
                    <p className="text-muted-foreground">Manage user requests for Point of Sale functionality</p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 font-bold px-3 py-1">
                        {requests.filter(r => r.status === 'pending').length} Pending Requests
                    </Badge>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ShoppingBag className="w-5 h-5" />
                        Request List
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {requests.length === 0 ? (
                        <div className="text-center py-12">
                            <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                            <p className="text-muted-foreground text-lg">No POS access requests found</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User / Type</TableHead>
                                    <TableHead>Business / Reason</TableHead>
                                    <TableHead>Locality</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {requests.map((request) => (
                                    <TableRow key={request.id}>
                                        <TableCell>
                                            <div>
                                                <div className="font-medium flex items-center gap-2">
                                                    {request.user_name}
                                                    <Badge variant="outline" className="text-[10px] uppercase font-bold py-0 h-4">
                                                        {request.user_type}
                                                    </Badge>
                                                </div>
                                                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                                    <Phone className="w-3 h-3" />
                                                    {request.user_phone}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="max-w-[200px]">
                                                {request.user_type === 'seller' ? (
                                                    <>
                                                        <div className="text-sm font-medium truncate">{request.business_name}</div>
                                                        <div className="text-xs text-muted-foreground truncate italic">"{request.reason}"</div>
                                                    </>
                                                ) : (
                                                    <span className="text-sm text-muted-foreground italic">Customer - Direct Access</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {request.near_locality ? (
                                                <Badge variant="success" className="bg-green-50 text-green-700 border-green-200">
                                                    Near Locality
                                                </Badge>
                                            ) : (
                                                <div className="text-xs">
                                                    {request.location_details?.city}, {request.location_details?.state}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {getStatusBadge(request.status)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-xs text-muted-foreground">
                                                {formatDate(request.created_at)}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Dialog open={showDetailsDialog && selectedRequest?.id === request.id}
                                                    onOpenChange={(open) => {
                                                        setShowDetailsDialog(open);
                                                        if (!open) setSelectedRequest(null);
                                                    }}>
                                                    <DialogTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => setSelectedRequest(request)}
                                                        >
                                                            <Eye className="w-4 h-4 mr-1" />
                                                            View
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-2xl">
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <ShoppingBag className="w-5 h-5" />
                                                                POS Access Request Details
                                                            </DialogTitle>
                                                        </DialogHeader>

                                                        {selectedRequest && (
                                                            <div className="space-y-6">
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <Card>
                                                                        <CardHeader className="pb-2">
                                                                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                                                <User className="w-4 h-4 text-primary" />
                                                                                User Information
                                                                            </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-2 text-sm">
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">Name</label>
                                                                                <p className="font-medium">{selectedRequest.user_name}</p>
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">Phone</label>
                                                                                <p className="font-medium">{selectedRequest.user_phone}</p>
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">User Type</label>
                                                                                <Badge className="bg-primary/10 text-primary border-none uppercase text-[10px]">
                                                                                    {selectedRequest.user_type}
                                                                                </Badge>
                                                                            </div>
                                                                        </CardContent>
                                                                    </Card>

                                                                    <Card>
                                                                        <CardHeader className="pb-2">
                                                                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                                                <MapPin className="w-4 h-4 text-primary" />
                                                                                Location Details
                                                                            </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-2 text-sm">
                                                                            {selectedRequest.near_locality ? (
                                                                                <div className="bg-green-50 p-3 rounded-md border border-green-100 flex items-center gap-2">
                                                                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                                                                    <span className="text-green-800 font-medium">Near Locality (Pickup)</span>
                                                                                </div>
                                                                            ) : (
                                                                                <>
                                                                                    <div>
                                                                                        <label className="text-xs text-muted-foreground">Address</label>
                                                                                        <p className="font-medium">{selectedRequest.location_details?.address}</p>
                                                                                    </div>
                                                                                    <div>
                                                                                        <label className="text-xs text-muted-foreground">City/State</label>
                                                                                        <p className="font-medium">
                                                                                            {selectedRequest.location_details?.city}, {selectedRequest.location_details?.state} - {selectedRequest.location_details?.pincode}
                                                                                        </p>
                                                                                    </div>
                                                                                </>
                                                                            )}
                                                                        </CardContent>
                                                                    </Card>
                                                                </div>

                                                                {selectedRequest.user_type === 'seller' && (
                                                                    <Card>
                                                                        <CardHeader className="pb-2">
                                                                            <CardTitle className="text-sm font-medium flex items-center gap-2">
                                                                                <Building className="w-4 h-4 text-primary" />
                                                                                Business Information
                                                                            </CardTitle>
                                                                        </CardHeader>
                                                                        <CardContent className="space-y-4">
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">Business Name</label>
                                                                                <p className="font-medium text-base">{selectedRequest.business_name}</p>
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">Reason for Access</label>
                                                                                <p className="bg-muted p-3 rounded-md text-sm italic italic">
                                                                                    "{selectedRequest.reason}"
                                                                                </p>
                                                                            </div>
                                                                        </CardContent>
                                                                    </Card>
                                                                )}

                                                                <div className="flex items-center justify-between bg-muted/30 p-4 rounded-lg">
                                                                    <div>
                                                                        <p className="text-xs text-muted-foreground">Request Time</p>
                                                                        <p className="text-sm font-medium">{formatDate(selectedRequest.created_at)}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs text-muted-foreground text-right">Current Status</p>
                                                                        <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
                                                                    </div>
                                                                </div>

                                                                {selectedRequest.status === 'pending' && (
                                                                    <div className="flex gap-4 pt-4 border-t">
                                                                        <Button
                                                                            onClick={() => handleUpdateStatus(selectedRequest.id, 'approved')}
                                                                            className="flex-1 bg-green-600 hover:bg-green-700 h-11"
                                                                        >
                                                                            <CheckCircle className="w-5 h-5 mr-2" />
                                                                            Approve & Grant POS
                                                                        </Button>
                                                                        <Button
                                                                            variant="destructive"
                                                                            onClick={() => handleUpdateStatus(selectedRequest.id, 'rejected')}
                                                                            className="flex-1 h-11"
                                                                        >
                                                                            <XCircle className="w-5 h-5 mr-2" />
                                                                            Reject Request
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </DialogContent>
                                                </Dialog>

                                                {request.status === 'pending' && (
                                                    <div className="flex gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleUpdateStatus(request.id, 'approved')}
                                                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                            title="Quick Approve"
                                                        >
                                                            <CheckCircle className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleUpdateStatus(request.id, 'rejected')}
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            title="Quick Reject"
                                                        >
                                                            <XCircle className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default PosRequests;
